import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { notesApi, analyticsApi, insightsApi } from '@/lib/api';
import { 
  PenSquare, MessageCircle, TrendingUp, Calendar, 
  Lightbulb, ArrowRight, Flame
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import MoodTrendWidget from '@/components/MoodTrendWidget';

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, notesRes, insightsRes] = await Promise.all([
          analyticsApi.get(),
          notesApi.getAll({ limit: 3 }),
          insightsApi.getAll()
        ]);
        setStats(analyticsRes.data);
        setRecentNotes(notesRes.data);
        setInsights(insightsRes.data.slice(0, 2));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard_greeting_morning');
    if (hour < 18) return t('dashboard_greeting_afternoon');
    return t('dashboard_greeting_evening');
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-500/20 text-green-400';
      case 'negative': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-cyan animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-white mb-2">
          {getGreeting()}, <span className="text-cyan">{user?.name?.split(' ')[0] || 'there'}</span>
        </h1>
        <p className="text-slate-400">{t('dashboard_overview')}</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/dashboard/journal">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="glass rounded-2xl p-6 cursor-pointer group hover:border-cyan/30 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan/10 flex items-center justify-center group-hover:bg-cyan/20 transition-colors">
                <PenSquare className="text-cyan" size={24} />
              </div>
              <div>
                <h3 className="text-white font-medium text-lg">{t('dashboard_new_entry')}</h3>
                <p className="text-slate-400 text-sm">{t('dashboard_new_entry_desc')}</p>
              </div>
              <ArrowRight className="ml-auto text-slate-500 group-hover:text-cyan transition-colors" />
            </div>
          </motion.div>
        </Link>

        <Link to="/dashboard/chat">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="glass rounded-2xl p-6 cursor-pointer group hover:border-gold/30 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                <MessageCircle className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="text-white font-medium text-lg">{t('dashboard_chat')}</h3>
                <p className="text-slate-400 text-sm">{t('dashboard_chat_desc')}</p>
              </div>
              <ArrowRight className="ml-auto text-slate-500 group-hover:text-gold transition-colors" />
            </div>
          </motion.div>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-cyan" size={18} />
              <span className="text-slate-400 text-sm">{t('dashboard_avg_mood')}</span>
            </div>
            <p className="text-3xl font-heading text-white">{stats?.average_mood || '—'}</p>
          </CardContent>
        </Card>

        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="text-cyan" size={18} />
              <span className="text-slate-400 text-sm">{t('dashboard_total_notes')}</span>
            </div>
            <p className="text-3xl font-heading text-white">{stats?.total_notes || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="text-gold" size={18} />
              <span className="text-slate-400 text-sm">{t('dashboard_streak')}</span>
            </div>
            <p className="text-3xl font-heading text-white">{stats?.streak_days || 0} <span className="text-lg">{t('dashboard_days')}</span></p>
          </CardContent>
        </Card>

        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className={stats?.improvement_percentage >= 0 ? 'text-green-400' : 'text-red-400'} size={18} />
              <span className="text-slate-400 text-sm">{t('dashboard_progress')}</span>
            </div>
            <p className="text-3xl font-heading text-white">
              {stats?.improvement_percentage >= 0 ? '+' : ''}{stats?.improvement_percentage || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Notes & Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mood Trend */}
        <MoodTrendWidget />

        {/* Recent Notes */}
        <Card className="bg-navy-900/40 border-white/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-lg">{t('dashboard_recent')}</CardTitle>
            <Link to="/dashboard/notes">
              <Button variant="ghost" size="sm" className="text-cyan hover:text-cyan-300">
                {t('dashboard_view_all')}
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentNotes.length === 0 ? (
              <p className="text-slate-500 text-center py-4">{t('dashboard_no_notes')}</p>
            ) : (
              recentNotes.map((note) => (
                <div
                  key={note.id}
                  className="p-4 rounded-xl bg-navy-950/50 border border-white/5 hover:border-cyan/20 transition-colors"
                >
                  <p className="text-slate-200 text-sm line-clamp-2">{note.content || note.transcript}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {note.sentiment && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getSentimentColor(note.sentiment)}`}>
                        {note.sentiment}
                      </span>
                    )}
                    <span className="text-xs text-slate-500">
                      {new Date(note.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Smart Insights */}
        <Card className="bg-navy-900/40 border-white/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Lightbulb className="text-gold" size={20} />
              {t('dashboard_insights')}
            </CardTitle>
            <Link to="/dashboard/insights">
              <Button variant="ghost" size="sm" className="text-gold hover:text-gold-muted">
                {t('dashboard_view_all')}
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {insights.length === 0 ? (
              <p className="text-slate-500 text-center py-4">{t('dashboard_no_insights')}</p>
            ) : (
              insights.map((insight) => (
                <div
                  key={insight.id}
                  className="p-4 rounded-xl bg-gradient-to-br from-gold/5 to-transparent border border-gold/10"
                >
                  <h4 className="text-gold font-medium text-sm mb-1">{insight.title}</h4>
                  <p className="text-slate-300 text-sm">{insight.description}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
